import React, { createContext, useContext, useState, useMemo } from 'react';
import { usePresentation } from './PresentationContext';

// Import local matrices
import defaultEn from '../locales/default-en.json';
import defaultFr from '../locales/default-fr.json';
import defaultSw from '../locales/default-sw.json';
//import expertEn from '../locales/expert-en.json';
//import expertFr from '../locales/expert-fr.json';
//import simpleEn from '../locales/simple-en.json';

const BUNDLES = {
  'default-en': defaultEn,
  'default-fr': defaultFr,
  'default-sw': defaultSw,
  //'expert-en': expertEn,
  //'expert-fr': expertFr,
  //'simple-en': simpleEn
};

const I18nContext = createContext(null);

export const I18nProvider = ({ language = 'en', children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(language);
  const { mode } = usePresentation();

  const t = useMemo(() => {
    return (key, params = {}) => {
      const keys = key.split('.');

      const resolveFromBundle = (bundleKey) => {
        let value = BUNDLES[bundleKey];
        if (!value) return null;
        for (const k of keys) {
          value = value?.[k];
          if (value === undefined) return null;
        }
        return value;
      };

      // 1. Specific mode + language
      let translated = resolveFromBundle(`${mode}-${currentLanguage}`);

      // 2. Default mode + language fallback
      if (!translated && mode !== 'default') {
        translated = resolveFromBundle(`default-${currentLanguage}`);
      }

      // 3. System ground truth fallback (default-en)
      if (!translated) {
        translated = resolveFromBundle('default-en');
      }

      // 4. Return original key if no match found
      if (!translated || typeof translated !== 'string') {
        return key;
      }

      // Variable interpolation (e.g., {{name}})
      Object.entries(params).forEach(([paramKey, paramVal]) => {
        translated = translated.replace(new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g'), paramVal);
      });

      return translated;
    };
  }, [currentLanguage, mode]);

  const value = {
    language: currentLanguage,
    setLanguage: setCurrentLanguage,
    t,
    availableLanguages: ['en', 'fr', 'sw']
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used within I18nProvider');
  return context;
};
